/**
 * ActingAsContext
 * ---------------
 * Lets an admin "act as" one of their managed partner brands.
 * When a partner is selected, any code that needs an org id should call
 * `useEffectiveOrganizationId()` — it returns the acted-as org if set,
 * otherwise falls back to the caller's own profile.organization_id.
 *
 * All server-side authority still lives in the edge functions — this hook
 * only decides which `X-Acting-As-Org` header (if any) to attach. The edge
 * function is required to validate that:
 *   - caller has app_role='admin'
 *   - target org is `is_managed=true` AND `managed_by_org_id = caller.organization_id`
 *
 * State is persisted to localStorage so a browser refresh keeps the acting-as
 * selection alive, but is cleared automatically when the user logs out (see
 * useAuth signOut path).
 */
import * as React from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

const STORAGE_KEY = "creatorvault.actingAs";

export interface ActingAsTarget {
  orgId: string;
  orgName: string;
  logoUrl: string | null;
}

interface ActingAsContextValue {
  actingAs: ActingAsTarget | null;
  isActingAs: boolean;
  /** Only callable by platform admins. Ignored for non-admins. */
  startActingAs: (target: ActingAsTarget) => void;
  stopActingAs: () => void;
}

const ActingAsContext = React.createContext<ActingAsContextValue | null>(null);

function readStored(): ActingAsTarget | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.orgId === "string" && typeof parsed.orgName === "string") {
      return { orgId: parsed.orgId, orgName: parsed.orgName, logoUrl: parsed.logoUrl ?? null };
    }
    return null;
  } catch {
    return null;
  }
}

export function ActingAsProvider({ children }: { children: React.ReactNode }) {
  const { isAdmin, user, adminRoleLoading } = useAuth();
  const [actingAs, setActingAs] = React.useState<ActingAsTarget | null>(() => readStored());

  // Clear acting-as when the user logs out or is confirmed non-admin.
  //
  // We MUST wait for `adminRoleLoading` to finish before acting on `!isAdmin`.
  // On a fresh tab load, `isAdmin` starts false and only flips true once the
  // `has_role` RPC resolves. Clearing during that window wipes a legitimately
  // persisted acting-as target — which is exactly the bug that made the WG
  // A2J Applicants sidebar entry never render after opening a community in a
  // new tab.
  React.useEffect(() => {
    if (!user) {
      if (actingAs !== null) {
        setActingAs(null);
        localStorage.removeItem(STORAGE_KEY);
      }
      return;
    }
    if (adminRoleLoading) return;
    if (!isAdmin && actingAs !== null) {
      setActingAs(null);
      localStorage.removeItem(STORAGE_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isAdmin, adminRoleLoading]);

  const startActingAs = React.useCallback(
    (target: ActingAsTarget) => {
      if (!isAdmin) {
        console.warn("[actingAs] startActingAs called by non-admin — ignored");
        return;
      }
      setActingAs(target);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(target));
      } catch {
        /* ignore quota errors */
      }
      // Fire-and-forget audit log. Best-effort; do not block the UX on it.
      // RLS on admin_org_actions permits admins to insert their own rows.
      // Cast because admin_org_actions was added in Phase 1 and the generated
      // Supabase types haven't been regenerated yet.
      if (user?.id) {
        ((supabase as any).from("admin_org_actions"))
          .insert({
            admin_user_id: user.id,
            target_org_id: target.orgId,
            action_type: "start_acting_as",
            entity_type: "organization",
            entity_id: target.orgId,
            payload: { orgName: target.orgName },
          })
          .then(({ error }: { error: any }) => {
            if (error) console.warn("[actingAs] audit insert failed:", error.message);
          });
      }
    },
    [isAdmin, user?.id]
  );

  const stopActingAs = React.useCallback(() => {
    setActingAs(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const value = React.useMemo<ActingAsContextValue>(
    () => ({
      actingAs,
      isActingAs: !!actingAs,
      startActingAs,
      stopActingAs,
    }),
    [actingAs, startActingAs, stopActingAs]
  );

  return <ActingAsContext.Provider value={value}>{children}</ActingAsContext.Provider>;
}

export function useActingAs(): ActingAsContextValue {
  const ctx = React.useContext(ActingAsContext);
  if (!ctx) {
    // Fail-soft: if the provider is missing, act as though nobody is acting-as.
    // This keeps components using the hook from crashing on unmount edges.
    return {
      actingAs: null,
      isActingAs: false,
      startActingAs: () => {},
      stopActingAs: () => {},
    };
  }
  return ctx;
}

/**
 * Returns the org id that operations should be scoped to.
 * When an admin is acting-as, this is the partner org id; otherwise it's
 * whatever the caller's own profile lists.
 */
export function useEffectiveOrganizationId(): string | null {
  const { actingAs } = useActingAs();
  const { profile } = useAuth();
  return actingAs?.orgId ?? profile?.organization_id ?? null;
}

/**
 * Returns headers to attach to `supabase.functions.invoke(...)` calls that
 * should honor the acting-as selection. Currently a single header:
 *   X-Acting-As-Org: <partnerOrgId>
 * Server MUST re-validate admin + managed-by-operator ownership.
 */
export function useActingAsHeaders(): Record<string, string> {
  const { actingAs } = useActingAs();
  if (!actingAs) return {};
  return { "X-Acting-As-Org": actingAs.orgId };
}
